<template>
  <div class="bg-base-100 rounded-lg p-6">
    <div
      class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8"
    >
      <h2 class="text-xl font-semibold">Kelionių sąrašas</h2>
      <div class="flex gap-2">
        <!-- DataFilter integration with reference data from store -->
        <DataFilter v-model="filters" title="Filtrai">
          <!-- Date range filters -->
          <div class="grid grid-cols-2 gap-2 mb-2">
            <fieldset class="fieldset">
              <legend class="fieldset-legend text-sm">Data nuo</legend>
              <input
                type="date"
                v-model="filters.date_from"
                class="input w-full"
              />
            </fieldset>
            <fieldset class="fieldset">
              <legend class="fieldset-legend text-sm">Data iki</legend>
              <input
                type="date"
                v-model="filters.date_to"
                class="input w-full"
              />
            </fieldset>
          </div>

          <!-- Status filter -->
          <fieldset class="fieldset mb-2">
            <legend class="fieldset-legend text-sm">Statusas</legend>
            <select
              v-model="filters.status"
              class="select w-full"
            >
              <option value="">Visi statusai</option>
              <option
                v-for="(config, status) in refStore.travelStatusConfig"
                :key="status"
                :value="status"
              >
                {{ config.text }}
              </option>
            </select>
          </fieldset>

          <!-- Organization filters -->
          <fieldset class="fieldset mb-2">
            <legend class="fieldset-legend text-sm">Organizacija</legend>
            <select
              v-model="filters.organization"
              class="select w-full"
            >
              <option value="">Visos organizacijos</option>
              <option
                v-for="org in refStore.organizations"
                :key="org.id"
                :value="org.id"
              >
                {{ org.name }}
              </option>
            </select>
          </fieldset>

          <!-- Executing Organization filters -->
          <fieldset class="fieldset mb-2">
            <legend class="fieldset-legend text-sm">Vykdanti organizacija</legend>
            <select
              v-model="filters.organization_executing"
              class="select w-full"
            >
              <option value="">Visos organizacijos</option>
              <option
                v-for="org in refStore.organizations"
                :key="org.id"
                :value="org.id"
              >
                {{ org.name }}
              </option>
            </select>
          </fieldset>

          <!-- Vehicle filters -->
          <fieldset class="fieldset mb-2">
            <legend class="fieldset-legend text-sm">Transporto priemonė</legend>
            <select
              v-model="filters.vehicle"
              class="select w-full"
            >
              <option value="">Visos transporto priemonės</option>
              <option
                v-for="vehicle in refStore.vehicles"
                :key="vehicle.id"
                :value="vehicle.id"
              >
                {{ vehicle.model }}, {{ vehicle.reg_nr }}
              </option>
            </select>
          </fieldset>

          <!-- Driver filters -->
          <fieldset class="fieldset mb-2">
            <legend class="fieldset-legend text-sm">Vairuotojas</legend>
            <select
              v-model="filters.driver"
              class="select w-full"
            >
              <option value="">Visi vairuotojai</option>
              <option
                v-for="driver in refStore.drivers"
                :key="driver.id"
                :value="driver.id"
              >
                {{ driver.name }} {{ driver.surname }}
              </option>
            </select>
          </fieldset>
        </DataFilter>

        <button
          class="btn btn-sm btn-outline"
          @click="handleExport"
          :disabled="travelStore.isExporting"
          title="Atsisiųsti sąrašą .xlsx formatu"
        >
          <DefaultIcon name="download" size="w-4 h-4" class="mr-1" />
        </button>

        <router-link
          to="/trips/create"
          class="btn btn-sm btn-success"
          title="Registruoti naują kelionę"
        >
          <DefaultIcon name="plus" size="w-4 h-4" />
          Nauja
        </router-link>
      </div>
    </div>

    <div
      v-if="travelStore.isLoading || refStore.isLoading"
      class="text-center py-4"
    >
      <span class="loading loading-spinner loading-lg"></span>
    </div>

    <div v-else class="overflow-x-auto">
      <div v-if="travelStore.travels.length > 0">
        <table class="table w-full mb-3">
          <!-- Table content unchanged -->
          <thead>
            <tr>
              <th>Nr</th>
              <th class="w-28">Data, laikas</th>
              <th>Trukmė, atstumas</th>
              <th class="w-96">Suplanuoti pavėžėjimai</th>
              <th class="w-72">Transporto priemonė</th>
              <th>Statusas</th>
              <th>Organizuoja</th>
              <th>Veiksmai</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="travel in travelStore.travels" :key="travel.id">
              <td>{{ travel.id }}</td>
              <td>
                {{ travel.date }} <br />
                {{ travel.time_start }} - {{ travel.time_finish }}
              </td>
              <td>
                <div>
                  <span
                    :class="{
                      'badge badge-warning badge-sm':
                        travel.duration > 420,
                    }"
                  >
                    {{ formatDuration(travel.duration) }} val.
                  </span>
                </div>
                <div>
                  <span
                    :class="{
                      'badge badge-warning badge-sm':
                        travel.length > 400,
                    }"
                    >{{ travel.length }} km</span
                  >
                </div>
              </td>
              <td class="w-96">
                <ul class="list-none text-xs">
                  <li v-for="trip in travel.trips" :key="trip.id" class="mb-3">
                    <ul class="list-none text-xs mt-1">
                      <li v-for="waypoint in trip.waypoints" :key="waypoint.id">
                        {{ waypoint.time }} - {{ waypoint.address }}
                      </li>
                    </ul>
                    <div class="flex items-center gap-2 mt-1">
                      <span>Keleiviai:</span>
                      <div class="flex items-center gap-1">
                        <span>{{ trip.person_regular_count }}</span>
                        <DefaultIcon name="passenger" size="w-4 h-4" />
                      </div>
                      <div class="flex items-center gap-1">
                        <span>{{ trip.person_disabled_count }}</span>
                        <DefaultIcon name="wheelchair" size="w-4 h-4" />
                      </div>
                    </div>
                    <div class="text-xs mt-1">
                      <template
                        v-for="(client, index) in trip.clients"
                        :key="client.id"
                      >
                        <router-link
                          :to="`/clients/${client.id}/edit`"
                          class="hover:underline"
                        >
                          {{ client.name }} {{ client.surname }} </router-link
                        >{{ index < trip.clients.length - 1 ? ", " : "" }}
                      </template>
                    </div>
                    <div v-if="trip.child_seat_needed" class="mt-1">
                      Reikalinga vaikiška kėdutė
                    </div>
                    <div v-if="trip.wheelchair_needed" class="mt-1">
                      Reikalingas neįgaliojo vež.
                    </div>
                  </li>
                </ul>
              </td>
              <td>
                <div class="flex items-center gap-3">
                  <!-- Avatar/Image -->
                  <div class="avatar">
                    <div class="w-14 h-14 rounded-lg">
                      <img :src="travel.vehicle?.img_url" alt="Vehicle" />
                    </div>
                  </div>

                  <!-- Text -->
                  <div class="text-xs">
                    {{ travel.vehicle?.info }} ({{ travel.driver?.info }})
                    <br />
                    {{ travel.vehicle?.organization }}
                  </div>
                </div>
              </td>
              <td>
                <span
                  :class="[
                    'badge badge-sm',
                    refStore.travelStatusConfig[travel.status]?.class ||
                      'badge-ghost',
                  ]"
                >
                  {{
                    refStore.travelStatusConfig[travel.status]?.text ||
                    travel.status
                  }}
                </span>
              </td>
              <td class="text-xs">{{ travel.organization?.info }}</td>
              <td>
                <div class="flex items-center gap-2">
                  <router-link
                    :to="'/travels/' + travel.id + '/view'"
                    class="btn btn-sm btn-ghost btn-circle"
                    title="Peržiūrėti kelionės informacija"
                  >
                    <DefaultIcon name="magnifying" size="w-4 h-4" />
                  </router-link>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <Pagination :meta="travelStore.meta" />
      </div>
      <div v-else>
        <div v-if="activeFiltersCount > 0">
          Pagal pasirinktus parametrus kelionių nerasta
        </div>
        <div v-else>
          Kelionių dar nesukūrėte. Kurkite naują pavėžėjimo užsakymą ir
          suformuokite pirmąją kelionę.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useTravelStore } from "@/stores/travels";
import { useAuthStore } from "@/stores/auth";
import { useReferenceDataStore } from "@/stores/referenceData";
import { onMounted, computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useTimeManagement } from "@/composables/time/useTimeManagement";
import DefaultIcon from "@/components/icons/DefaultIcon.vue";
import Pagination from "@/components/ui/pagination/PaginationComponent.vue";
import DataFilter from "@/components/filters/DataFilter.vue";

const travelStore = useTravelStore();
const authStore = useAuthStore();
const refStore = useReferenceDataStore();

const route = useRoute();
const router = useRouter();
const { formatDuration, getTodayDate } = useTimeManagement();

// Extended filter values for v-model
const filters = ref({
  date_from: "",
  date_to: "",
  status: "",
  organization: "",
  organization_executing: "",
  vehicle: "",
  driver: "",
});

// Calculate active filters count
const activeFiltersCount = computed(() => {
  return Object.values(filters.value).filter((val) => val).length;
});

// Watch for URL changes to fetch data
watch(
  () => route.query,
  async (newQuery) => {
    await travelStore.fetchTravels(newQuery);
  },
);

onMounted(async () => {
  try {
    // Ensure reference data is loaded
    if (!refStore.isInitialized) {
      await refStore.fetchReferenceData();
    }

    // Set default parameters if none exist
    if (!route.query.page && !route.query.status) {
      await router.replace({
        query: {
          page: "1",
          date_from: getTodayDate(),
          status: "travelStatus1",
          organization_executing: authStore.user.organization_id,
        },
      });
    } else {
      await travelStore.fetchTravels(route.query);
    }
  } catch (error) {
    console.error("Failed to fetch travels:", error);
  }
});

const handleExport = () => {
  travelStore.exportTravels(route.query);
};
</script>
